import { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { CalendarIcon, CalendarPlus, LocationIcon } from 'assets/icons';
import { DateMenu } from 'common/date-picker/components/date-menu';
import { LocationMenu } from 'common/album-detail-modal/components/menus/location-menu';
import { classnames, getDateRange } from 'helpers/utils';
import styles from './entry-meta-fields.module.scss';

interface EntryMetaFieldsProps {
  location?: string;
  startDate?: string;
  endDate?: string;
  onDateChange: (startDate: string, endDate: string) => void;
  onLocationChange: (location: string) => void;
}

// Date-range + location selectors reused from the view page (DateMenu +
// LocationMenu), shown as tag buttons in the create-entry modal. The pickers
// open as floating popovers layered on top of the modal (portaled to the modal
// root) so they never push the form layout.
export const EntryMetaFields = ({
  location,
  startDate,
  endDate,
  onDateChange,
  onLocationChange,
}: EntryMetaFieldsProps) => {
  const [showDate, setShowDate] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  const dateBtnRef = useRef<HTMLButtonElement>(null);
  const locationBtnRef = useRef<HTMLButtonElement>(null);

  const range = getDateRange(startDate, endDate);
  const dateLabel = range.startDate
    ? `${range.startDate}${
        range.endDate && range.endDate !== range.startDate
          ? ` - ${range.endDate}`
          : ''
      }`
    : '';

  const renderPopover = (
    anchor: HTMLButtonElement | null,
    onClose: () => void,
    children: React.ReactNode,
    extraClass = '',
  ) => {
    const root = document.getElementById('modal');
    if (!anchor || !root) return null;
    const rect = anchor.getBoundingClientRect();
    return ReactDOM.createPortal(
      <>
        <div
          className={styles.backdrop}
          role="presentation"
          onClick={onClose}
        />
        <div
          className={classnames(styles.popover, extraClass)}
          style={{ top: rect.bottom + 8, left: rect.left }}
        >
          {children}
        </div>
      </>,
      root,
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.row}>
        <button
          type="button"
          ref={dateBtnRef}
          className={classnames(styles.tag, {
            [styles.tagActive]: showDate,
          })}
          onClick={() => {
            setShowDate((prev) => !prev);
            setShowLocation(false);
          }}
        >
          {dateLabel ? (
            <CalendarIcon className={styles.tagIcon} />
          ) : (
            <CalendarPlus className={styles.tagIcon} />
          )}
          <span className={styles.tagText}>{dateLabel || 'Add date'}</span>
        </button>

        <button
          type="button"
          ref={locationBtnRef}
          className={classnames(styles.tag, {
            [styles.tagActive]: showLocation,
          })}
          onClick={() => {
            setShowLocation((prev) => !prev);
            setShowDate(false);
          }}
        >
          <LocationIcon className={styles.tagIcon} />
          <span className={styles.tagText}>{location || 'Add location'}</span>
        </button>
      </div>

      {showDate &&
        renderPopover(
          dateBtnRef.current,
          () => setShowDate(false),
          <DateMenu
            renderInline
            subject="memory"
            currentDate={startDate}
            currentFinalDate={endDate}
            onUpdateDate={(start, end) => {
              onDateChange(start, end);
              setShowDate(false);
            }}
          />,
        )}

      {showLocation &&
        renderPopover(
          locationBtnRef.current,
          () => setShowLocation(false),
          <LocationMenu
            renderInline
            addLocation={(newLocation) => {
              onLocationChange(newLocation);
              setShowLocation(false);
            }}
          />,
          styles.locationPopover,
        )}
    </div>
  );
};
